import {
  AHSeparator,
  AHText,
  AHTextFonts,
  Box,
  Card,
  Circle,
  ListItem,
  SelectDot,
  TouchableBox,
  useBoxStyle,
  useTheme,
} from "arkh";

import React, { useCallback, useMemo } from "react";
import { SelectListItem } from "arkh/src/cross-platform/SelectList/SelectListItem";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import {
  ListSectionHeader,
  ListSectionHeaderTitle,
} from "../../../components/atoms/ListSectionHeader";
import { ScrollView } from "react-native";

import { KPIFillMode } from "../Edit/KPIEditParamList";
import { SegmentedControl } from "../../../components/SegmentedControl";
import { FormattedMessage } from "react-intl";
import { useWeekdays } from "../../../common/useWeekdays";

function WeekdaySelector(props: {
  weekdays: number[];
  setWeekdays(weekdays: number[]): void;
}) {
  const weekdays = useWeekdays("narrow");

  const selectedWeekdays = useMemo(
    () =>
      props.weekdays.length === 0 ? [0, 1, 2, 3, 4, 5, 6] : props.weekdays,
    [props.weekdays]
  );

  const renderWeekday = useCallback(
    function (wd: string, index: number) {
      const isSelected = selectedWeekdays.includes(index);
      return (
        <TouchableBox
          key={index}
          style={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
          onPress={() => {
            if (selectedWeekdays.includes(index)) {
              const newDays = selectedWeekdays.filter((wd) => wd !== index);

              props.setWeekdays(
                newDays.length === 1
                  ? [0, 1, 2, 3, 4, 5, 6].filter((k) => k !== index)
                  : newDays
              );
            } else {
              props.setWeekdays([...selectedWeekdays, index]);
            }
          }}
        >
          <Circle size="medium" color={isSelected ? "primary" : "background"}>
            <AHText
              type="subtitle"
              color={isSelected ? "lightText" : "darkerText"}
            >
              {wd}
            </AHText>
          </Circle>
        </TouchableBox>
      );
    },
    [props, selectedWeekdays]
  );

  return (
    <Box
      spaceTop="xxlarge"
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
      }}
    >
      {weekdays.map(renderWeekday)}
    </Box>
  );
}

export function KPIFrequencySelectorComponent(props: {
  selectedDays: number[];
  onSetSelectedDays: (selectedDays: number[]) => void;
  fillMode: KPIFillMode;
  onSetFillMode: (newFillMode: KPIFillMode) => void;
}) {
  const intl_tokens = useIntlTokens();
  const theme = useTheme();
  const isWeekly =
    props.selectedDays.length === 1 && props.selectedDays[0] === 0;
  const contentContainerStyle = useBoxStyle({
    space: "medium",
  });

  return (
    <ScrollView
      scrollEnabled={false}
      automaticallyAdjustContentInsets
      contentInsetAdjustmentBehavior="always"
      style={{ flex: 1 }}
      contentContainerStyle={contentContainerStyle}
    >
      <ListSectionHeader>
        <ListSectionHeaderTitle>
          <FormattedMessage
            defaultMessage="When do you want this KPI entered?"
            description="KPI Properties component frequency title"
          />
        </ListSectionHeaderTitle>
      </ListSectionHeader>
      <Box style={{ flexDirection: "row" }}>
        <TouchableBox
          space="medium"
          color="surface"
          style={{ flex: 1, borderBottomWidth: 0, borderWidth: 1 }}
          borderColor="border-primary"
          cornerRadius="large"
          cornerRadiusBottomLeft="none"
          cornerRadiusBottomRight="none"
          onPress={() => {
            if (isWeekly) {
              props.onSetSelectedDays([0, 1, 2, 3, 4, 5, 6]);
            }
          }}
        >
          <Box style={{ flex: 1 }}>
            <Box
              style={{
                flexDirection: "row",
                alignItems: "center",
                alignSelf: "stretch",
              }}
            >
              <AHText
                type="title-small"
                style={{ flex: 1 }}
                font={AHTextFonts.REGULAR}
              >
                {intl_tokens.Daily}
              </AHText>
              <SelectDot selected={!isWeekly} selectedDotColor="primary" />
            </Box>
            {!isWeekly ? (
              <WeekdaySelector
                weekdays={props.selectedDays}
                setWeekdays={props.onSetSelectedDays}
              />
            ) : null}
          </Box>
        </TouchableBox>
      </Box>
      <AHSeparator />
      <Box spaceBottom="xlarge">
        <SelectListItem
          selected={isWeekly}
          border="border-primary"
          onPress={() => {
            if (!isWeekly) {
              props.onSetSelectedDays([0]);
            }
          }}
          isLast
        >
          <ListItem.Title>{intl_tokens.Weekly}</ListItem.Title>
        </SelectListItem>
      </Box>

      <Card defaultBorder>
        <SegmentedControl
          appearance="light"
          style={{
            backgroundColor: "transparent",
          }}
          fontStyle={{
            color: theme.darkerText,
          }}
          activeFontStyle={{
            color: theme.surface,
          }}
          tintColor={theme.primary}
          selectedIndex={props.fillMode === "current" ? 0 : 1}
          onChange={(e) => {
            props.onSetFillMode(
              e.nativeEvent.selectedSegmentIndex === 0 ? "current" : "previous"
            );
          }}
          values={
            isWeekly
              ? [
                  intl_tokens.EnterThisWeeksValue,
                  intl_tokens.EnterLastWeeksValue,
                ]
              : [intl_tokens.EnterTodaysValue, intl_tokens.EnterYesterdaysValue]
          }
        />
      </Card>
    </ScrollView>
  );
}
